/**
 * Why a Skin Analysis status poll stopped answering — and therefore what the caller may do.
 *
 * The poll used to treat every failure the same way, which is how a lost session, a result that
 * does not exist, and a flaky network all ended up as one three-minute timeout followed by
 * "please retake your photos". They are three different situations and only one of them is
 * recoverable by waiting, so the classification lives here, once, for both the query factory
 * (which decides whether to keep polling) and the pipeline (which decides what to tell the user).
 */
import { isAxiosError } from 'axios';

export type SkinAnalysisPollFailureKind =
  /** 401/403 — the session is gone. Polling cannot recover; the user must re-authenticate. */
  | 'unauthenticated'
  /** 404 — this result does not belong to the caller (or no longer exists). Nothing to wait for. */
  | 'not_found'
  /** Network blip or 5xx. Worth retrying; the result may well still be on its way. */
  | 'transient';

/** HTTP status behind a failed poll, when the failure came from a response at all. */
function responseStatus(error: unknown): number | undefined {
  return isAxiosError(error) ? error.response?.status : undefined;
}

export function classifySkinAnalysisPollFailure(error: unknown): SkinAnalysisPollFailureKind {
  const status = responseStatus(error);
  if (status === 401 || status === 403) return 'unauthenticated';
  if (status === 404) return 'not_found';
  return 'transient';
}

/**
 * Should the poll give up on this error?
 *
 * Only for failures no amount of waiting fixes. Anything else — including an exhausted retry
 * budget on a 5xx — keeps polling, because the server may still complete the result and the
 * caller's own timeout is what ultimately bounds the wait.
 */
export function isSkinAnalysisPollStopped(error: unknown): boolean {
  return error != null && classifySkinAnalysisPollFailure(error) !== 'transient';
}
